import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Link } from 'components/core/links/C1Link'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { BaseActionForm } from 'components/pages/task/action/form/BaseActionForm'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { MetaSynchronousHeader } from 'lib/constants/platform'
import { routes } from 'lib/routes'
import { TaskActionsServiceCommentRequest } from 'pbts/c1/api/task/v1/actions'
import React, { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { commentOnTaskApi } from 'redux_toolkit/tasks'

export type CommentOnTaskDialogV2Props = {
    task: TaskValue
} & ClosableProps

export const CommentOnTaskDialogV2: FC<CommentOnTaskDialogV2Props> = ({ task, close }) => {
    const intl = useIntl()
    const toast = useAlert()
    const { mutate: addComment } = commentOnTaskApi()

    const handleSave = useCallback(
        (comment: string): void => {
            addComment(
                TaskActionsServiceCommentRequest.create({
                    taskId: task.id,
                    comment,
                    expandMask: { paths: ['*'] },
                }),
                MetaSynchronousHeader,
            )
                .then(() => {
                    toast(
                        <FormattedMessage
                            id="task_comment_success"
                            defaultMessage="Comment added to task {task_link}"
                            values={{
                                task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                            }}
                        />,
                        'success',
                    )
                    close()
                })
                .catch((err) => {
                    toast(
                        intl.formatMessage(
                            {
                                id: 'task_comment_error',
                                defaultMessage: 'Error adding comment to task. {code}: {msg}',
                            },
                            {
                                code: (err as RpcError).code,
                                msg: (err as RpcError).message,
                            },
                        ),
                        'error',
                    )
                })
        },
        [addComment, close, intl, task, toast],
    )

    return (
        <BaseActionForm
            closeDialog={close}
            open
            onComment={handleSave}
            title={intl.formatMessage({
                id: 'component_task_action_form_comment_title',
                defaultMessage: 'Comment on this task',
            })}
            description={intl.formatMessage({
                id: 'component_action_form_comment_description',
                defaultMessage: "Add your comment to this task's discussion.",
            })}
            actionText={intl.formatMessage({
                id: 'component_task_action_form_comment_submit',
                defaultMessage: 'Comment',
            })}
        />
    )
}
